import React, {createContext, useContext, useCallback} from "react";
import useApiService from "./ApiService";
import CONFIG from "./CONFIG";

const RecordRouteApiContext = createContext(null);

export function RecordRouteApiProvider({children}) {

  const {get, post, put, del} = useApiService(CONFIG.SERVER_ORGANIZATION);
  const {get: get_channels} = useApiService(CONFIG.SERVER_CHANNEL);
  const {get: get_agents} = useApiService(CONFIG.SERVER_ASSISTANT);
  const {get: get_documents} = useApiService(CONFIG.SERVER_DOCUMENT);

  const getRecordRoute = useCallback(async (skip, limit) => {
    if (!skip) {
      skip = 0;
    }
    if (!limit) {
      limit = 10;
    }
    try {
      const response = await get(
        `/v1/record_route?skip=${skip}&limit=${limit}`
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching record route:", error);
      throw error;
    }
  }, [get]);

  const getRecordRouteById = useCallback(async (recordRouteId) => {
    try {
      const response = await get(`/v1/record_route/${recordRouteId}`);
      return response.data;
    } catch (error) {
      console.error("Error fetching record route by ID:", error);
      throw error;
    }
  }, [get]);

  const createRecordRoute = useCallback(async (recordRoute) => {
    try {
      const response = await post(`/v1/record_route`, recordRoute);
      return response.data;
    } catch (error) {
      console.error("Error creating record route:", error);
      throw error;
    }
  }, [post]);

  const updateRecordRouteById = useCallback(async (recordRouteId, recordRoute) => {
    try {
      const response = await put(`/v1/record_route/${recordRouteId}`, recordRoute);
      return response.data;
    } catch (error) {
      console.error("Error updating record route:", error);
      throw error;
    }
  }, [put]);

  const triggerEventById = useCallback(async (recordRouteId) => {
    try {
      const response = await post(`/v1/record_route/${recordRouteId}/trigger`);
      return response.data;
    } catch (error) {
      console.error("Error trigger record route:", error);
      throw error;
    }
  }, [post]);

  const deleteRecordRoute = useCallback(async (recordRouteId) => {
    try {
      const response = await del(`/v1/record_route/${recordRouteId}`);
      return response.data;
    } catch (error) {
      console.error("Error deleting record route:", error);
      throw error;
    }
  }, [del]);

  const getChannels = useCallback(async () => {
    try {
      const response = await get_channels(`/v1/channels`);
      return response.data.items ?? [];
    } catch (error) {
      console.error("Error fetching channels:", error);
      throw error;
    }
  }, [get_channels]);

  const getAgents = useCallback(async () => {
    try {
      const response = await get_agents(`/v1/assistants`);
      return response.data.items ?? [];
    } catch (error) {
      console.error("Error fetching agents:", error);
      throw error;
    }
  }, [get_agents]);

  const getDocumentCollections = useCallback(async () => {
    try {
      const response = await get_documents(`/v1/document_collection`);
      return response.data.items ?? [];
    } catch (error) {
      console.error("Error fetching document collections:", error);
      throw error;
    }
  }, [get]);

  const getDocuments = useCallback(async () => {
    try {
      const response = await get_documents(`/v1/documents`);
      return response.data.items ?? [];
    } catch (error) {
      console.error("Error fetching document:", error);
      throw error;
    }
  }, [get]);

  const getZendeskConnections = useCallback(async () => {
    try {
      const response = await get(
        `/v1/connections/zendesk`
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching connection:", error);
      throw error;
    }
  }, [get]);

  const getCreatioConnections = useCallback(async () => {
    try {
      const response = await get(
        `/v1/connections/creatio`
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching connection:", error);
      throw error;
    }
  }, [get]);

  const getNotionConnections = useCallback(async () => {
    try {
      const response = await get(
        `/v1/connections/notion`
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching connection:", error);
      throw error;
    }
  }, [get]);

    const getGoogleConnections = useCallback(async () => {
      try {
        const response = await get(
          `/v1/connections/google`
        );
        return response.data;
      } catch (error) {
        console.error("Error fetching connection:", error);
        throw error;
      }
    }, [get]);

  const getConnectionObjects = useCallback(async () => {
    try {
      const response = await get(
        `/v1/connection_objects`
      );
      return response.data.items;
    } catch (error) {
      console.error("Error fetching connection objects:", error);
      throw error;
    }
  }, [get]);

  return (
    <RecordRouteApiContext.Provider value={{
      getRecordRoute,
      getRecordRouteById,
      createRecordRoute,
      updateRecordRouteById,
      deleteRecordRoute,
      getChannels,
      getAgents,
      getDocuments,
      getDocumentCollections,
      getCreatioConnections,
      getZendeskConnections,
      getNotionConnections,
      triggerEventById,
      getGoogleConnections,
      getConnectionObjects,
    }}>
      {children}
    </RecordRouteApiContext.Provider>
  );
}

export function useRecordRouteApi() {
  const context = useContext(RecordRouteApiContext);
  if (!context) {
    throw new Error("useRecordRouteApi must be used within a RecordRouteApiProvider");
  }
  return context;
}
